/**
 * Mock XMPP Backend
 * Simulates XMPP protocol using browser storage (localForage or localStorage)
 */
import { createStorage } from './storage';
import { getBareJid, buildJid, generateMessageId, getISOTimestamp, delay } from './helpers';
import { XMPPAdapter } from './adapters/xmpp-adapter';
// ============================================================================
// Mock XMPP Backend Implementation
// ============================================================================
/**
 * Mock XMPP Backend
 *
 * Note: This is a partial implementation for testing. It implements the app-specific
 * helper methods from XMPPBackend but doesn't include all Agent properties.
 * Type assertion is used when returning this as XMPPBackend.
 */
export class MockXMPPBackend {
    constructor(config) {
        this.handlers = {};
        this.connectionState = 'disconnected';
        this.config = config;
        this.latency = config.mockLatency ?? 100;
        this.storage = createStorage({
            backend: config.mockPersistence || 'localStorage',
            debug: config.mockDebug ?? false,
            namespace: config.mockNamespace || 'war-rooms', // Use unified namespace
        });
        // Initialize XMPP adapter
        this.adapter = new XMPPAdapter(this.storage, config.domain);
    }
    // ===== Connection Management =====
    connect(opts) {
        // Extract credentials from opts or config
        const username = opts?.jid?.split('@')[0] || this.config.username;
        if (!username) {
            throw new Error('Username required for connection');
        }
        this.updateConnectionState('connecting');
        // Async connection in background
        void delay(this.latency).then(async () => {
            this.updateConnectionState('authenticating');
            await delay(this.latency);
            // Build full JID with resource
            const resource = opts?.resource || this.config.resource || 'web';
            const fullJid = buildJid(username, this.config.domain, resource);
            const bareJid = getBareJid(fullJid);
            this.currentJid = fullJid;
            // Store session
            await this.storage.setItem('session', {
                jid: fullJid,
                bareJid,
                username,
                connectedAt: getISOTimestamp(),
            });
            this.updateConnectionState('authenticated');
            // Send initial presence
            this.sendPresence();
            // Emit session:started event (Stanza convention)
            this.handlers.onConnectionStateChange?.(this.getConnectionInfo());
        });
    }
    disconnect() {
        this.updateConnectionState('disconnecting');
        void delay(this.latency).then(async () => {
            // Send unavailable presence
            await this.sendUnavailable();
            // Clear session
            await this.storage.removeItem('session');
            delete this.currentJid;
            this.updateConnectionState('disconnected');
        });
    }
    getConnectionInfo() {
        const info = {
            state: this.connectionState,
        };
        if (this.currentJid) {
            info.jid = this.currentJid;
            info.bareJid = getBareJid(this.currentJid);
        }
        return info;
    }
    setEventHandlers(handlers) {
        this.handlers = { ...this.handlers, ...handlers };
    }
    // ===== Roster Operations =====
    async getRoster() {
        await delay(this.latency);
        // Use adapter to get all users
        const users = await this.adapter.getAllUsers();
        // Convert XMPPUser[] to RosterResult format
        const items = users.map((user) => {
            const item = {
                jid: user.jid,
                subscription: 'both', // Mock: all users have mutual subscription
                groups: user.groups,
            };
            if (user.name)
                item.name = user.name;
            return item;
        });
        return { items };
    }
    async updateRosterItem(item) {
        await delay(this.latency);
        const bareJid = getBareJid(item.jid);
        const username = bareJid.split('@')[0];
        if (!username) {
            throw new Error('Invalid JID format');
        }
        // Get existing from unified storage
        const existing = await this.storage.getItem(`entities/users/${username}`);
        if (!existing) {
            // Create new if doesn't exist
            const unifiedUser = {
                username,
                jid: bareJid,
                name: item.name || username,
                groups: item.groups || [],
                createdAt: new Date().toISOString(),
            };
            await this.storage.setItem(`entities/users/${username}`, unifiedUser);
            // Update user index
            const userIndex = (await this.storage.getItem('entities/users/_index')) || [];
            if (!userIndex.includes(username)) {
                userIndex.push(username);
                await this.storage.setItem('entities/users/_index', userIndex);
            }
        }
        else {
            // Update existing
            const updated = {
                ...existing,
                groups: item.groups ?? existing.groups,
                name: item.name !== undefined ? item.name : existing.name,
            };
            await this.storage.setItem(`entities/users/${username}`, updated);
        }
    }
    async removeRosterItem(jid) {
        await delay(this.latency);
        const bareJid = getBareJid(jid);
        const username = bareJid.split('@')[0];
        if (!username) {
            throw new Error('Invalid JID format');
        }
        // Remove from unified storage
        await this.storage.removeItem(`entities/users/${username}`);
        // Update user index
        const userIndex = (await this.storage.getItem('entities/users/_index')) || [];
        const newIndex = userIndex.filter((u) => u !== username);
        await this.storage.setItem('entities/users/_index', newIndex);
    }
    // ===== Presence Operations =====
    sendPresence(pres) {
        if (!this.currentJid) {
            throw new Error('Not connected');
        }
        const presence = pres || {
            from: this.currentJid,
        };
        // Generate ID if not provided
        if (!presence.id) {
            presence.id = generateMessageId();
        }
        // Set from if not provided
        if (!presence.from) {
            presence.from = this.currentJid;
        }
        // Async operations in background
        void delay(this.latency).then(() => {
            // Store own presence
            void this.storage.setItem('presence/self', presence);
        });
        // Broadcast to contacts (simulated)
        // In real XMPP, server broadcasts to subscribed contacts
        return presence.id;
    }
    async sendUnavailable() {
        if (!this.currentJid) {
            return;
        }
        await delay(this.latency);
        await this.storage.removeItem('presence/self');
    }
    async subscribePresence(jid) {
        await delay(this.latency);
        // In mock, presence subscriptions are automatically approved
        // Unified storage doesn't track subscription state separately
        // (all roster items are considered 'both' subscription)
        const bareJid = getBareJid(jid);
        const username = bareJid.split('@')[0];
        // Verify user exists in unified storage
        const user = await this.storage.getItem(`entities/users/${username}`);
        if (!user) {
            throw new Error(`User not found: ${jid}`);
        }
        // In unified storage, subscription state is implicit (all users have 'both')
        // No update needed
    }
    // ===== Direct Messaging =====
    sendMessage(msg) {
        if (!this.currentJid) {
            throw new Error('Not connected');
        }
        const message = {
            ...msg,
            id: msg.id || generateMessageId(),
            from: msg.from || this.currentJid,
            type: msg.type || 'chat',
        };
        // Async operations in background
        void delay(this.latency).then(() => {
            // Store in archive based on type
            if (message.type === 'groupchat') {
                const roomJid = getBareJid(message.to || '');
                void this.storage.setItem(`archive/rooms/${roomJid}/${message.id}`, message);
            }
            else {
                void this.storage.setItem(`archive/direct/${message.id}`, message);
            }
            // Trigger message handler
            this.handlers.onMessage?.(message);
        });
        return message.id; // Guaranteed to be set above
    }
    // ===== Multi-User Chat Operations =====
    async joinRoom(jid, nick, _opts) {
        if (!this.currentJid) {
            throw new Error('Not connected');
        }
        await delay(this.latency);
        // Store room membership
        await this.storage.setItem(`rooms/${jid}/joined`, {
            nickname: nick,
            joinedAt: getISOTimestamp(),
        });
        // Add self as occupant
        const occupant = {
            nick,
            jid: this.currentJid,
            affiliation: 'member',
            role: 'participant',
        };
        await this.storage.setItem(`rooms/${jid}/occupants/${nick}`, occupant);
        // Return MUC presence (self-presence confirming join)
        const mucPresence = {
            from: `${jid}/${nick}`,
            to: this.currentJid,
            type: undefined, // available presence
            muc: {
                type: 'info', // MUC info type
                statusCodes: ['110'], // self-presence code (string in Stanza)
                affiliation: 'member',
                role: 'participant',
                jid: this.currentJid,
            },
        };
        return mucPresence;
    }
    async leaveRoom(jid, nick, _opts) {
        await delay(this.latency);
        // Get nickname - use provided or lookup stored
        let nickname = nick;
        if (!nickname) {
            const membership = await this.storage.getItem(`rooms/${jid}/joined`);
            nickname = membership?.nickname;
        }
        if (nickname) {
            // Remove self as occupant
            await this.storage.removeItem(`rooms/${jid}/occupants/${nickname}`);
        }
        // Remove room membership
        await this.storage.removeItem(`rooms/${jid}/joined`);
        // Return unavailable presence
        const presence = {
            from: `${jid}/${nickname || ''}`,
            to: this.currentJid,
            type: 'unavailable',
        };
        return presence;
    }
    async sendGroupchatMessage(roomJid, body) {
        if (!this.currentJid) {
            throw new Error('Not connected');
        }
        // Get nickname
        const membership = await this.storage.getItem(`rooms/${roomJid}/joined`);
        if (!membership) {
            throw new Error(`Not joined to room: ${roomJid}`);
        }
        const from = `${roomJid}/${membership.nickname}`;
        const message = {
            id: generateMessageId(),
            from,
            to: roomJid,
            type: 'groupchat',
            body,
        };
        // Use sendMessage for consistency
        return this.sendMessage(message);
    }
    async getRoomInfo(roomJid) {
        await delay(this.latency);
        // Extract room name from JID (e.g., "red-command@conference.local" -> "red-command")
        const roomName = roomJid.split('@')[0];
        if (!roomName) {
            throw new Error(`Invalid room JID: ${roomJid}`);
        }
        // Use adapter to get room
        const room = await this.adapter.getRoom(roomName);
        if (!room) {
            throw new Error(`Room not found: ${roomJid}`);
        }
        // Return just the DiscoInfo part
        return room.info;
    }
    async getRoomOccupants(roomJid) {
        await delay(this.latency);
        const occupants = await this.storage.getAll(`rooms/${roomJid}/occupants/`);
        return Object.values(occupants);
    }
    async getMyRooms() {
        if (!this.currentJid) {
            throw new Error('Not connected');
        }
        await delay(this.latency);
        // Use adapter to get rooms for current user
        const rooms = await this.adapter.getUserRooms(this.currentJid);
        // Return full XMPPRoom objects (jid + info + extension)
        return rooms;
    }
    async setRoomSubject(roomJid, subject) {
        await delay(this.latency);
        // Store subject
        await this.storage.setItem(`rooms/${roomJid}/subject`, subject);
    }
    async getRoomConfig(roomJid) {
        await delay(this.latency);
        const config = await this.storage.getItem(`rooms/${roomJid}/config`);
        return config || {};
    }
    async setRoomConfig(roomJid, config) {
        await delay(this.latency);
        await this.storage.setItem(`rooms/${roomJid}/config`, config);
    }
    async setAffiliation(roomJid, jid, affiliation, reason) {
        await delay(this.latency);
        // Store affiliation
        await this.storage.setItem(`rooms/${roomJid}/affiliations/${jid}`, {
            affiliation,
            reason,
            updatedAt: getISOTimestamp(),
        });
    }
    async setRole(roomJid, nickname, role, _reason) {
        await delay(this.latency);
        // Update occupant role
        const occupant = await this.storage.getItem(`rooms/${roomJid}/occupants/${nickname}`);
        if (occupant) {
            occupant.role = role;
            await this.storage.setItem(`rooms/${roomJid}/occupants/${nickname}`, occupant);
        }
    }
    // ===== Message Archive Management =====
    async queryArchive(roomJid, query) {
        await delay(this.latency);
        // Get all messages for room
        const allMessages = await this.storage.getAll(`archive/rooms/${roomJid}/`);
        let messages = Object.values(allMessages);
        // Apply limit from paging
        const limit = query.paging?.max || 50;
        messages = messages.slice(0, limit);
        // Convert messages to MAMResult format (simplified for mock)
        // Only include messages with delay timestamps (required for MAM)
        const results = messages
            .filter((msg) => msg.delay !== undefined)
            .map((msg) => ({
            version: '2',
            queryId: query.queryId || 'query1',
            id: msg.id,
            item: {
                delay: msg.delay,
                message: msg,
            },
        }));
        // Return MAMFin with results
        const fin = {
            type: 'result',
            version: '2',
            complete: true,
            results,
            paging: {
                count: messages.length,
            },
        };
        return fin;
    }
    // ===== PubSub Operations =====
    // (Basic implementation - will be expanded in T015-T016)
    async subscribePubSub(node) {
        await delay(this.latency);
        await this.storage.setItem(`pubsub/subscriptions/${node}`, {
            subscribedAt: getISOTimestamp(),
        });
    }
    async unsubscribePubSub(node) {
        await delay(this.latency);
        await this.storage.removeItem(`pubsub/subscriptions/${node}`);
    }
    async publishPubSub(node, payload, itemId) {
        await delay(this.latency);
        const id = itemId || generateMessageId();
        await this.storage.setItem(`pubsub/nodes/${node}/items/${id}`, {
            id,
            payload,
            publishedAt: getISOTimestamp(),
            publisher: this.currentJid,
        });
        // Trigger notification for subscribers
        this.handlers.onPubSubNotification?.(node, payload);
        return id;
    }
    async retrievePubSub(node, maxItems) {
        await delay(this.latency);
        const items = await this.storage.getAll(`pubsub/nodes/${node}/items/`);
        let result = Object.values(items);
        if (maxItems) {
            result = result.slice(0, maxItems);
        }
        return result;
    }
    async deletePubSubItem(node, itemId) {
        await delay(this.latency);
        await this.storage.removeItem(`pubsub/nodes/${node}/items/${itemId}`);
    }
    async createPubSubNode(node, config) {
        await delay(this.latency);
        await this.storage.setItem(`pubsub/nodes/${node}/config`, config || {});
    }
    async deletePubSubNode(node) {
        await delay(this.latency);
        // Delete node config and all items
        await this.storage.removeItem(`pubsub/nodes/${node}/config`);
        const items = await this.storage.keys(`pubsub/nodes/${node}/items/`);
        for (const item of items) {
            await this.storage.removeItem(item);
        }
    }
    // ===== Chat States =====
    async sendChatState(_to, _state, _isGroupchat = false) {
        await delay(this.latency);
        // Chat states are typically not stored, just broadcast
        // In a real implementation, this would send a message stanza with chatstate
    }
    // ===== Private Helpers =====
    updateConnectionState(state) {
        this.connectionState = state;
        this.handlers.onConnectionStateChange?.(this.getConnectionInfo());
    }
}
//# sourceMappingURL=mock-xmpp.js.map